import type { ReactNode } from "react";
import type { Participant } from "../protocol";
import { t, money, word } from "../i18n";
import type { SessionState } from "../timeline";

// A share's side panel, the same for everyone in it, top to bottom: the
// session's status (once it has said anything) and its alerts; the people;
// the owner's policy; and last, filling what height is left, the chat
// (components/Chat, byj-1wv.51.3).

/**
 * A side panel's section folded to one line — its title, what it holds in
 * brief, and a chevron that turns as it opens — the whole line pressable;
 * `className` in place of the side panel's own, for a fold elsewhere (the
 * student's class page, byj-1wv.51.6); `open`, it starts open (an exam's
 * steps for a student who has started nothing, byj-1wv.51.12); `onToggle`,
 * told each time it is opened or folded, for a page that keeps which (the
 * Machines page's Inside a machine, byj-1wv.51.15).
 */
export function FoldPanel({ label, summary, children, className = "panel", open = false, onToggle, testid }: {
  label: string;
  summary: ReactNode;
  children: ReactNode;
  className?: string;
  open?: boolean;
  onToggle?: (open: boolean) => void;
  testid?: string;
}) {
  return (
    <section className={className} aria-label={label} data-testid={testid}>
      <details className="fold" open={open || undefined} onToggle={onToggle && ((e) => onToggle(e.currentTarget.open))}>
        <summary>
          <span className="fold-title">{label}</span>
          <span className="fold-summary">{summary}</span>
          <svg className="fold-chevron" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </summary>
        {children}
      </details>
    </section>
  );
}

/** The session's status, once it has reported one — folded to its state, turns and cost — and its alerts. */
export function StatusPanels({ state, onJump }: { state: SessionState; onJump: (seqs: readonly number[]) => void }) {
  const st = state.status;
  return (
    <>
      {/* a session that reports no status (an agent between calls, an
          ended share) has no Status, rather than one loading for ever */}
      {st && (
        <FoldPanel
          label={t("session:status")}
          summary={
            <>
              <span className={`state state-${st.state}`}>{word("session:state", st.state) ?? st.state}</span>
              <span className="muted small">{t("session:statusBrief", { count: st.turns, cost: money(st.cost_usd) })}</span>
              {st.pending_permissions > 0 && <span className="chip chip-warn">{t("session:pendingBrief", { count: st.pending_permissions })}</span>}
            </>
          }
        >
          <dl className="status">
            <dt>{t("session:status")}</dt>
            <dd>
              <span className={`state state-${st.state}`}>{word("session:state", st.state) ?? st.state}</span>
            </dd>
            <dt>{t("session:turns")}</dt>
            <dd>{st.turns}</dd>
            <dt>{t("session:cost")}</dt>
            <dd>{money(st.cost_usd)}</dd>
            <dt>{t("session:pendingPermissions")}</dt>
            <dd className={st.pending_permissions > 0 ? "warn" : undefined}>{st.pending_permissions}</dd>
          </dl>
        </FoldPanel>
      )}

      <section className="panel">
        <h3>{t("session:alerts")}</h3>
        {state.alerts.length === 0 ? (
          <p className="muted">{t("session:noAlerts")}</p>
        ) : (
          <ul className="alerts">
            {state.alerts.map((a, i) => (
              <li key={i}>
                <button className="link alert" onClick={() => onJump(a.body.seqs ?? [])}>
                  <strong>{word("session:alertKind", a.body.kind) ?? a.body.kind}</strong>
                  {a.body.detail && <span className="muted"> — {a.body.detail}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

/**
 * A side panel's section under its heading: how many it holds, when it is a
 * number of anything, and what acts on it at the heading's end — a share's
 * people (PeoplePanel), a file's people, versions and folder (byj-1wv.51.17).
 */
export function Panel({ label, count, action, className, testid, children }: {
  label: string;
  count?: number;
  action?: ReactNode;
  className?: string;
  testid?: string;
  children: ReactNode;
}) {
  return (
    <section className={`panel${className ? ` ${className}` : ""}`} aria-label={label} data-testid={testid}>
      <div className="panel-head">
        <h3>
          {label} {count !== undefined && <span className="tab-count">{count}</span>}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * Who is in it, with how many, and the owner's Invite over them: a share's
 * people, or a recording's reviewers (`label`).
 */
export function PeoplePanel({ children, count, invite, label = t("session:people") }: { children: ReactNode; count: number; invite?: ReactNode; label?: string }) {
  return (
    <Panel label={label} count={count} action={invite} className="people-panel">
      {children}
    </Panel>
  );
}

export function People({
  participants,
  me,
  onKick,
  keyboard,
  onKeyboard,
}: {
  participants: Participant[];
  me: string;
  onKick?: (user: string) => void;
  /** The coach holding a terminal share's keyboard (RFC-043 model 21). */
  keyboard?: string;
  /** The owner hands the keyboard to a coach, or takes it back with "". */
  onKeyboard?: (user: string) => void;
}) {
  if (participants.length === 0) return <p className="muted">{t("session:nobody")}</p>;
  return (
    <ul className="people">
      {participants.map((p) => (
        <li key={p.user}>
          <span className={`badge badge-${p.role}`}>
            <span className="badge-role">{word("common:role", p.role) ?? p.role}</span>
            <span className="badge-name">{p.name}</span>
          </span>
          {p.user === me && <span className="muted small"> ({t("common:you")})</span>}
          {keyboard !== undefined && keyboard !== "" && p.user === keyboard && <span className="chip">{t("session:hasKeyboard")}</span>}
          {onKeyboard && p.role === "coach" && (
            <button className="small" onClick={() => onKeyboard(p.user === keyboard ? "" : p.user)}>
              {p.user === keyboard ? t("session:takeKeyboard") : t("session:giveKeyboard")}
            </button>
          )}
          {onKick && p.user !== me && p.role !== "owner" && (
            <button className="small danger" onClick={() => onKick(p.user)}>
              {t("session:kick")}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
